import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Prediction {
  id: string
  metric: string
  name: string
  current_value: number
  predicted_value: number
  predicted_change_percent: number
  confidence_interval: { low: number; high: number }
  confidence_score: number
  prediction_horizon: string
  model_type: string
  factors: { name: string; impact: string; weight: number }[]
  last_updated: string
}

interface Model {
  id: string
  name: string
  type: string
  accuracy: number
  last_trained: string
  status: string
}

export default function PredictiveAnalyticsPage() {
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [models, setModels] = useState<Model[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedPrediction, setSelectedPrediction] = useState<Prediction | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [predRes, modRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/predictive-analytics/predictions'),
      fetch(API_BASE + '/predictive-analytics/models'),
      fetch(API_BASE + '/predictive-analytics/statistics')
    ])
    if (predRes.ok) setPredictions((await predRes.json()).predictions || [])
    if (modRes.ok) setModels((await modRes.json()).models || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const formatValue = (value: number, metric: string) => {
    if (metric.includes('revenue') || metric.includes('cost')) {
      return '$' + value.toLocaleString()
    }
    if (metric.includes('rate') || metric.includes('churn')) {
      return value.toFixed(1) + '%'
    }
    if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M'
    if (value >= 1000) return (value / 1000).toFixed(0) + 'K'
    return value.toString()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Predictive Analytics Dashboard</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Predictions</div>
              <div className="text-2xl font-bold">{statistics.total_predictions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Confidence</div>
              <div className="text-2xl font-bold text-green-600">{(statistics.average_confidence * 100).toFixed(0)}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Models</div>
              <div className="text-2xl font-bold">{statistics.active_models}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Positive Outlook</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.positive_predictions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">High Confidence</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.high_confidence_predictions}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 mb-8">
          {predictions.map(pred => (
            <div key={pred.id} className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelectedPrediction(pred)}>
              <div className="text-sm text-gray-500 mb-1">{pred.name}</div>
              <div className="flex items-end gap-4 mb-3">
                <div className="text-3xl font-bold">{formatValue(pred.predicted_value, pred.metric)}</div>
                <div className={`text-lg font-semibold ${pred.predicted_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {pred.predicted_change_percent >= 0 ? '+' : ''}{pred.predicted_change_percent.toFixed(1)}%
                </div>
              </div>
              <div className="flex justify-between text-sm text-gray-500 mb-2">
                <span>Current: {formatValue(pred.current_value, pred.metric)}</span>
                <span>{pred.prediction_horizon.replace('_', ' ')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: `${pred.confidence_score * 100}%` }}></div>
                </div>
                <span className="text-sm font-medium">{(pred.confidence_score * 100).toFixed(0)}%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Prediction Models</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accuracy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Trained</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {models.map(model => (
                  <tr key={model.id}>
                    <td className="px-6 py-4 font-medium">{model.name}</td>
                    <td className="px-6 py-4 text-sm">{model.type.replace(/_/g, ' ')}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full" style={{ width: `${model.accuracy * 100}%` }}></div>
                        </div>
                        <span className="text-sm">{(model.accuracy * 100).toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(model.last_trained).toLocaleDateString()}</td>
                    <td className="px-6 py-4"><span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">{model.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedPrediction && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">{selectedPrediction.name} Factors</h3>
              <div className="space-y-3">
                {selectedPrediction.factors.map((factor, i) => (
                  <div key={i} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-sm">{factor.name}</span>
                      <span className={`text-sm font-semibold ${factor.impact === 'positive' ? 'text-green-600' : factor.impact === 'negative' ? 'text-red-600' : 'text-gray-600'}`}>
                        {factor.weight > 0 ? '+' : ''}{(factor.weight * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${factor.impact === 'positive' ? 'bg-green-500' : factor.impact === 'negative' ? 'bg-red-500' : 'bg-gray-400'}`}
                           style={{ width: `${Math.abs(factor.weight) * 100}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
